/**
 * Meta API Service
 * 
 * Centralized service for Meta Ads connection management
 * Used by Analytics Summary, Budget Allocation, and Creatives features
 */

import apiClient from '@/shared/lib/api'

// Response wrapper type
interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

// Connection status from API
export interface MetaConnectionStatus {
  connected: boolean
  account_id?: string
  account_name?: string
  currency?: string
  status?: string
  connected_at?: string
  last_sync_at?: string
  message?: string
}

// Ad account from Meta
export interface MetaAdAccount {
  id: string
  account_id: string
  name: string
  currency: string
}

// Connect request
export interface MetaConnectRequest {
  access_token: string
  account_id: string
  account_name?: string
  currency?: string
}

// Connect response
export interface MetaConnectResponse {
  success: boolean
  message: string
  account_id?: string
  account_name?: string
  currency?: string
}

// Sync response
export interface MetaSyncResponse {
  success: boolean
  message: string
  synced_at: string
}

/**
 * Meta API Service - Centralized Meta connection management
 */
export const metaApi = {
  /**
   * Get current Meta connection status
   */
  async getConnectionStatus(): Promise<ApiResponse<MetaConnectionStatus>> {
    try {
      const response = await apiClient.get<MetaConnectionStatus>('/meta/connection')
      return { success: true, data: response.data }
    } catch (error: unknown) {
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch connection status' }
    }
  },

  /**
   * Connect a Meta ad account
   */
  async connect(request: MetaConnectRequest): Promise<ApiResponse<MetaConnectResponse>> {
    try {
      const response = await apiClient.post<MetaConnectResponse>('/meta/connect', request)
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('[MetaAPI] Error connecting Meta:', error)
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      return { 
        success: false, 
        error: err.response?.data?.message || err.message || 'Failed to connect' 
      }
    }
  },

  /**
   * Disconnect the current Meta account
   */
  async disconnect(): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>('/meta/connection')
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('[MetaAPI] Error disconnecting:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to disconnect' }
    }
  },

  /**
   * Trigger a full sync of Meta data (90 days)
   * Use smartSync() for faster incremental updates
   */
  async sync(): Promise<ApiResponse<MetaSyncResponse>> {
    try {
      const response = await apiClient.post<MetaSyncResponse>('/meta/sync')
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('[MetaAPI] Error syncing:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to sync data' }
    }
  },

  /**
   * Trigger a smart sync of Meta data (incremental - only missing data)
   * This is much faster than full sync as it only fetches yesterday, today, and gaps
   */
  async smartSync(): Promise<ApiResponse<MetaSyncResponse>> {
    try {
      const response = await apiClient.post<MetaSyncResponse>('/meta/smart-sync')
      return { success: true, data: response.data }
    } catch (error: unknown) {
      console.error('[MetaAPI] Error smart syncing:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to sync data' }
    }
  },

  /**
   * Get available ad accounts for a given access token
   */
  async getAdAccounts(accessToken: string): Promise<ApiResponse<MetaAdAccount[]>> {
    try {
      const response = await apiClient.get<{ ad_accounts: MetaAdAccount[] }>('/meta/ad-accounts', {
        params: { access_token: accessToken },
      })
      return { success: true, data: response.data.ad_accounts }
    } catch (error: unknown) {
      console.error('[MetaAPI] Error fetching ad accounts:', error)
      const err = error as { message?: string }
      return { success: false, error: err.message || 'Failed to fetch ad accounts' }
    }
  },
}

export default metaApi

